import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { formatDate } from "../lib/format.js";
import { Link } from "../lib/router.js";
import { PageSpinner, EmptyState } from "../components/ui.js";
import { BmiGauge } from "../components/BmiGauge.js";
import { TrendChart } from "../components/TrendChart.js";
import { TrendExplorer } from "../components/TrendExplorer.js";
import { QuickLogModal } from "../components/QuickLogModal.js";
import { IconProgress } from "../components/icons.js";

interface Overview {
  hasHeight: boolean;
  currentPhase: string | null;
  inRecovery: boolean;
  trend: {
    count: number;
    latest: number | null;
    starting: number | null;
    smoothedCurrent: number | null;
    change: number | null;
    changePct: number | null;
    ratePerWeek: number | null;
    series: { date: string; weight: number; smoothed: number }[];
  };
  gauge: null | {
    currentBmi: number; startingBmi: number | null; change: number | null;
    category: { key: string; label: string; min: number; max: number };
    nextThreshold: { bmi: number; label: string; weightLb: number; lbToGo: number } | null;
    categories: { key: string; label: string; min: number; max: number }[];
  };
  forecast: { available: boolean; reason?: string; rangeText?: string; targetLb?: number | null };
  waistToHeight: number | null;
  waistToHeightHealthy: boolean | null;
  milestones: { id: string; title: string; detail: string | null; achievedAt: string }[];
  thenNow: { label: string; then: string; now: string; change: string }[];
  daysBuilding: number;
}

const PHASE_ROADMAP = [
  ["recovery", "Recovery", "Heal first — walking, hydration, protein, rest."],
  ["foundation", "Foundation", "Build movement, nutrition routine, and walking capacity."],
  ["fat_loss", "Fat loss", "Weight and waist trend down; strength develops."],
  ["recomposition", "Recomposition", "Build chest, shoulders, arms, back while leaning out."],
  ["muscle_building", "Muscle building", "Add size and strength where you want it."],
  ["lean_out", "Lean out", "Reveal the definition you've built."],
  ["maintenance", "Maintenance", "Keep it — and refine."]
] as const;

export function ProgressPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logWeight, setLogWeight] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Overview>("/api/body/overview"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load progress.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (!data) {
    return (
      <div>
        <div className="page-header"><h1>Progress</h1></div>
        {error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}
      </div>
    );
  }

  const hasWeight = data.trend.count > 0;
  const currentPhase = data.currentPhase ?? (data.inRecovery ? "recovery" : "foundation");

  return (
    <div>
      <div className="page-header">
        <div className="row-between">
          <div>
            <h1>Progress</h1>
            {data.daysBuilding > 0 && (
              <p className="subtitle">You've been building for {data.daysBuilding} days.</p>
            )}
          </div>
          <button className="btn btn-primary" onClick={() => setLogWeight(true)}>
            Log weigh-in
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!data.hasHeight && (
        <div className="alert alert-info">
          Add your height in <Link to="/life-profile">Life Profile</Link> to see your BMI and gauge.
        </div>
      )}

      {!hasWeight ? (
        <EmptyState icon={<IconProgress />} title="Log your first weigh-in" action={
          <button className="btn btn-sm mt-4" onClick={() => setLogWeight(true)}>Log weight</button>
        }>
          Your BMI gauge, weight journey, and milestones appear here once you start logging. We track the trend, never a single reading.
        </EmptyState>
      ) : (
        <div className="stack">
          {data.gauge && <BmiGauge gauge={data.gauge} />}

          {/* Weight journey */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Weight journey</span>
              <span className="dim text-sm">trend-smoothed</span>
            </div>
            <div className="stat-grid" style={{ marginBottom: "var(--space-4)" }}>
              <div className="stat"><div className="stat-label">Started</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{data.trend.starting} lb</div></div>
              <div className="stat"><div className="stat-label">Trend now</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{data.trend.smoothedCurrent} lb</div></div>
              <div className="stat"><div className="stat-label">Change</div><div className="stat-value" style={{ fontSize: "var(--text-xl)", color: (data.trend.change ?? 0) <= 0 ? "var(--success)" : "var(--text-primary)" }}>{(data.trend.change ?? 0) > 0 ? "+" : ""}{data.trend.change} lb</div></div>
            </div>
            <TrendChart line={data.trend.series.map((s) => ({ date: s.date, value: s.smoothed }))} raw={data.trend.series.map((s) => ({ date: s.date, value: s.weight }))} unit=" lb" />
          </div>

          {/* Trend explorer — weight, nutrition, activity, mood, adherence */}
          <TrendExplorer />

          {/* Waist-to-height */}
          {data.waistToHeight !== null && (
            <div className="card">
              <div className="card-header"><span className="card-title">Waist-to-height ratio</span></div>
              <div className="row-between">
                <div className="stat-value">{data.waistToHeight}</div>
                <span className={data.waistToHeightHealthy ? "badge badge-success" : "badge badge-warning"}>
                  {data.waistToHeightHealthy ? "Healthy (under 0.5)" : "Above 0.5"}
                </span>
              </div>
              <p className="dim text-xs mt-2" style={{ marginBottom: 0 }}>Keeping your waist under half your height is a simple, strong health marker.</p>
            </div>
          )}

          {/* Scale journey / forecast */}
          <div className="card">
            <div className="card-header"><span className="card-title">Scale journey</span></div>
            {data.inRecovery ? (
              <p className="muted text-sm">You're in Recovery Mode — this isn't a weight-loss phase. Forecasts resume once you're building again.</p>
            ) : data.forecast.available ? (
              <>
                <p>Toward your provisional target of <strong>{data.forecast.targetLb} lb</strong>:</p>
                <p className="stat-value" style={{ fontSize: "var(--text-xl)", marginTop: 4 }}>{data.forecast.rangeText}</p>
                <p className="dim text-xs mt-2" style={{ marginBottom: 0 }}>An estimate from your recent trend — a range, not a promise. It updates as you log more, and your target itself can be refined as your physique develops.</p>
              </>
            ) : (
              <p className="muted text-sm">
                {data.forecast.reason === "no_target"
                  ? "Set a provisional target in your goals to see an estimated range."
                  : data.forecast.reason === "target_reached"
                    ? "You're at or past your provisional target — time to reassess what body you're building."
                    : "Keep logging — an estimated range appears once there's enough trend."}
              </p>
            )}
          </div>

          {/* Then -> Now */}
          {data.thenNow.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Then → Now</span></div>
              <div className="table-card" style={{ border: "none", padding: 0 }}>
                {data.thenNow.map((row) => (
                  <div className="tc-row" key={row.label}>
                    <span className="tc-label">{row.label}</span>
                    <span><span className="dim">{row.then}</span> → <strong>{row.now}</strong> <span className="dim">({row.change})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Milestones */}
          {data.milestones.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Milestones</span></div>
              <div className="stack">
                {data.milestones.map((m) => (
                  <div className="row" key={m.id} style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
                    <span className="log-tile-icon" style={{ width: 32, height: 32, flexShrink: 0 }}>★</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{m.title}</div>
                      {m.detail && <div className="dim text-sm">{m.detail}</div>}
                      <div className="dim text-xs">{formatDate(m.achievedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Where am I going */}
          <div className="card">
            <div className="card-header"><span className="card-title">Where am I going?</span></div>
            <p className="dim text-xs" style={{ marginTop: -4 }}>The route can change; the destination gets refined as you build.</p>
            <div className="roadmap mt-4">
              {PHASE_ROADMAP.map(([key, label, desc]) => {
                const active = key === currentPhase;
                return (
                  <div className={`roadmap-step${active ? " active" : ""}`} key={key}>
                    <div className="roadmap-dot" />
                    <div>
                      <div style={{ fontWeight: active ? 700 : 500, color: active ? "var(--accent)" : "var(--text-primary)" }}>
                        {label}{active ? " — you are here" : ""}
                      </div>
                      <div className="dim text-sm">{desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {logWeight && (
        <QuickLogModal type="weight" onClose={() => setLogWeight(false)} onLogged={load} />
      )}
    </div>
  );
}
